import { ReactNode } from "react";
import { Rule } from "antd/es/form";
import { BaseRecord, ParsedFilter } from "types";
import { ColumnsType, TableProps } from "antd/es/table";
import { QueryParams } from "./queryParams.types";

export type DataIndex = string | number | (string | number)[];

export enum InputType {
  TEXT = "text",
  NUMBER = "number",
  SELECT = "select",
  DATE = "date",
  TEXTAREA = "textarea",
  SELECT_ONLY = "selectOnly",
}

export interface SelectOption {
  label: string;
  value: string | number;
}

export enum FilterType {
  SEARCH = "search",
  SELECT = "select",
  MULTI_SELECT = "multiSelect",
}

export interface TableEditColumn<T = unknown> {
  title: string;
  dataIndex: DataIndex;
  key?: string;
  editable?: boolean;
  sorter?: boolean | ((a: T, b: T) => number);
  filterable?: boolean;
  width?: number | string;
  align?: "left" | "right" | "center";
  inputType?: InputType;
  selectOptions?: SelectOption[];
  filterType?: FilterType;
  filterOptions?: SelectOption[];
  render?: (value: unknown, record: T, index: number) => ReactNode;
  fixed?: "left" | "right";
  rules?: Rule[];
}

export interface TableRecord {
  key: string | number;
  [key: string]: unknown;
}

export interface TableCommonProps<T extends TableRecord = TableRecord> {
  columns: TableEditColumn<T>[];
  data: T[];
  onChangeData?: (newData: T[]) => void;
  onAddRow?: (newRow: Partial<T>) => void;
  onDeleteRow?: (key: string | number) => void;
  onSave?: (key: string | number, record: T) => void;
  loading?: boolean;
  pagination?: boolean;
  rowSelection?: boolean;
  addRowText?: string;
  bordered?: boolean;
  type?: "CHI_PHI";
  editable?: boolean;
  onApprove?: (record: T) => void;
  onReject?: (record: T) => void;
  newRowDefaults?: Partial<Omit<T, "key">>;
}

export interface EditableCellProps<T = unknown> {
  editing: boolean;
  dataIndex: DataIndex;
  title: string;
  inputType: InputType;
  record: T;
  index: number;
  children: ReactNode;
  selectOptions?: SelectOption[];
  rules?: Rule[];
}

export interface FilterDropdownProps {
  setSelectedKeys: (keys: React.Key[]) => void;
  selectedKeys: React.Key[];
  confirm: () => void;
  clearFilters?: () => void;
  close?: () => void;
}

export interface ActionColumnProps<T = TableRecord> {
  record: T;
  isEditing: boolean;
  edit: (key: string | number) => void;
  save: (key: string | number) => Promise<boolean>;
  cancel: () => void;
  deleteRow: (key: string | number) => void;
  type?: "CHI_PHI";
  onApprove?: (record: T) => void;
  onReject?: (record: T) => void;
}
export interface ActionsConfig {
  edit?: boolean;
  delete?: boolean;
  copy?: boolean;
  download?: boolean;
}
export interface TableViewCommonProps<T extends BaseRecord<unknown>> {
  columns: ColumnsType<T>;
  dataSource: T[];
  searchable?: boolean;
  actions?: ActionsConfig;
  onEdit?: (record: T) => void;
  onView?: (record: T) => void;
  onDelete?: (record: T) => void;
  onCopy?: (record: T) => void;
  onDownload?: (record: T) => void;
  displayView?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: string[];
  tableProps?: Omit<TableProps<T>, 'columns' | 'dataSource' | 'pagination' | 'onChange'>;
  count?: number;
  onPaginationChange: (query: QueryParams) => void;
  handleToggleStatus?: (checked: boolean, record: T) => void;
  currentPage?: number;
  searchColumnDefault?: ParsedFilter[];
  rowSelection?: TableProps<T>['rowSelection'];
  downLoadLoading?: boolean;
  idFileDownload?: string;
}

export interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  pageSizeOptions?: string[];
  onChange?: (page: number, pageSize?: number) => void;
  onPageSizeChange?: (current: number, size: number) => void;
  showTotal?: boolean;
  totalTextFormat?: (current: number, pageSize: number, total: number) => string;
  showSizeChanger?: boolean;
  pageSizeText?: string;
  maxVisiblePages?: number;
  className?: string;
}

export interface Columns {
  title: string;
  key: string;
}

export interface RenderResultObject {
  children: React.ReactNode;
  props?: Record<string, unknown>;
}